import { useState } from "react";
import Logo from "../../public/ITDept.jpeg";
import { useNavigate, useLocation, Navigate } from "react-router-dom";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { Mail, Lock, LogIn, ShieldCheck, Sparkles } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "../context/AuthContext";
import { SpotlightCard } from "../components/ui/SpotlightCard";
import { Spinner } from "../components/ui/FullScreenLoader";
import { apiError } from "../lib/api";

const schema = z.object({
  email: z.string().email("Enter a valid email"),
  password: z.string().min(1, "Password is required"),
});

type LoginForm = z.infer<typeof schema>;

export default function Login() {
  const { login, isAuthenticated, loading } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const from =
    (location.state as { from?: { pathname?: string } })?.from?.pathname ||
    "/dashboard";

  const [submitting, setSubmitting] = useState(false);

  const {
    register,
    handleSubmit,
    setValue,
    formState: { errors },
  } = useForm<LoginForm>({
    resolver: zodResolver(schema),
    defaultValues: { email: "", password: "" },
  });

  if (!loading && isAuthenticated) return <Navigate to={from} replace />;

  const onSubmit = async (data: LoginForm) => {
    setSubmitting(true);
    try {
      await login(data.email, data.password);
      toast.success("Welcome back!");
      navigate(from, { replace: true });
    } catch (err) {
      toast.error(apiError(err));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="relative z-10 min-h-screen flex items-center justify-center p-4">
      <div className="w-full max-w-5xl grid lg:grid-cols-2 gap-6">
        {/* Brand panel */}
        <SpotlightCard
          padded={false}
          spotlightColor="rgba(0, 86, 255, 0.25)"
          className="hidden lg:flex flex-col justify-between p-8 overflow-hidden"
        >
          {/* Floating orbs */}
          <div
            aria-hidden
            className="absolute -top-24 -left-24 w-72 h-72 rounded-full opacity-60 blur-3xl animate-float-orb"
            style={{ background: "radial-gradient(circle, #000eb5 0%, transparent 70%)" }}
          />
          <div
            aria-hidden
            className="absolute -bottom-24 -right-24 w-72 h-72 rounded-full opacity-50 blur-3xl animate-float-orb"
            style={{
              background: "radial-gradient(circle, #fb0b14 0%, transparent 70%)",
              animationDelay: "4s",
            }}
          />

          <div className="relative">
            <div className="mt-8 flex items-center gap-3">
              <img src={Logo} alt="Logo" className="w-14 h-14 rounded-2xl shadow-glow object-cover" />
              <div>
                <h1 className="text-3xl font-bold text-white tracking-tight">
                  IMPACT Pro
                </h1>
                <p className="text-sm text-slate-300">IT Support Ticketing System</p>
              </div>
            </div>
            <div className="relative grid grid-cols-2 gap-3 mt-10">
              <div className="glass-subtle rounded-2xl p-4">
                <div className="text-2xl font-bold text-white">4 tiers</div>
                <div className="text-xs text-slate-400 mt-1">Critical → Low</div>
              </div>
              <div className="glass-subtle rounded-2xl p-4">
                <div className="text-2xl font-bold text-white">Auto SLA</div>
                <div className="text-xs text-slate-400 mt-1">Escalation cron</div>
              </div>
              <div className="glass-subtle rounded-2xl p-4">
                <div className="text-2xl font-bold text-white">RBAC</div>
                <div className="text-xs text-slate-400 mt-1">Agent · IT · Admin</div>
              </div>
              <div className="glass-subtle rounded-2xl p-4">
                <div className="text-2xl font-bold text-white">Reports</div>
                <div className="text-xs text-slate-400 mt-1">Live dashboards</div>
              </div>
            </div>
          </div>
        </SpotlightCard>

        {/* Login form */}
        <SpotlightCard padded={false} spotlightColor="rgba(0, 86, 255, 0.25)" className="p-8">
          <div className="flex items-center gap-2 text-slate-300 text-xs uppercase tracking-widest">
            <ShieldCheck className="w-3.5 h-3.5" /> Secure sign-in
          </div>
          <h2 className="mt-2 text-2xl font-semibold text-white">Welcome back</h2>
          <p className="mt-1 text-sm text-slate-400">
            Use your work email to access the dashboard.
          </p>

          <form onSubmit={handleSubmit(onSubmit)} className="mt-6 space-y-4">
            <div>
              <label className="label">Email</label>
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400 pointer-events-none" />
                <input
                  type="email"
                  autoComplete="email"
                  className="input pl-10"
                  placeholder="you@company.com"
                  {...register("email")}
                />
              </div>
              {errors.email && <p className="field-error">{errors.email.message}</p>}
            </div>

            <div>
              <label className="label">Password</label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400 pointer-events-none" />
                <input
                  type="password"
                  autoComplete="current-password"
                  className="input pl-10"
                  placeholder="••••••••"
                  {...register("password")}
                />
              </div>
              {errors.password && (
                <p className="field-error">{errors.password.message}</p>
              )}
            </div>

            <button type="submit" disabled={submitting} className="btn-primary w-full">
              {submitting ? <Spinner /> : <LogIn className="w-4 h-4" />}
              Sign in
            </button>
          </form>

          {/* System build identifier — do not modify */}
          <div
            className="mt-8 text-center select-none transition-opacity duration-700"
            style={{ opacity: 0.04 }}
            onMouseEnter={(e) => (e.currentTarget.style.opacity = "0.35")}
            onMouseLeave={(e) => (e.currentTarget.style.opacity = "0.04")}
          >
            <p className="text-[9px] text-slate-400 tracking-widest uppercase">
              Crafted by JCFP & SAD
            </p>
          </div>

        </SpotlightCard>
      </div>
    </div>
  );
}
